import { EmptyState } from '@synoptic/ui-kit/empty-state/empty-state.tsx';
import { TrashIcon } from '@synoptic/ui-kit/icons/react/trash.tsx';
import { clsx } from 'clsx';
import { FC } from 'react';
import { useMatch, useNavigate } from 'react-router';
import {
  CollectionStreamPostFragmentFragment,
  StreamPostCardFragmentFragment,
} from '@/__generated__/graphql.ts';
import { useHighlightNewPost } from '@/components/post-card/new-post/use-highlight-new-post.ts';
import { paths } from '@/routes-utils/paths.ts';
import { handlePostCardClick } from '@/utils/handle-post-card-click';
import { formatDateWithMs } from '../../date-utils/format-date-with-ms.ts';
import { formatDate } from '../../date-utils/format-date.ts';
import { LayoutCard } from '../layout/card.tsx';
import { PostCardShareActionsMenu } from '../post-card/post-card-actions/post-card-share-actions-menu.tsx';
import { PostCardHeader } from '../post-card/post-card-header.tsx';
import { PostCardMedia } from '../post-card/post-card-media/post-card-media.tsx';
import {
  postCardActions,
  postCardBody,
  postCardBodyWithDisabledLinks,
  postCardContainer,
  postCardContent,
  postCardDate,
  postCardDateContainer,
  postCardDeletedPlaceholder,
  postCardFooter,
  postCardLabels,
} from '../post-card/post-card.css.ts';
import { StreamLabel } from '../post-label/stream-label.tsx';
import {
  isStreamDeckSettingEnabled,
  StreamDeckSettings,
} from '../stream-deck/customize-stream-deck-dropdown/use-stream-deck-settings.ts';
import { HighlightPattern } from '../text-editor/plugins/highlight/types';
import { useHighlightMatchSummary } from '../text-editor/plugins/highlight/use-highlight-match-summary';
import { TextEditor } from '../text-editor/text-editor.tsx';
import { ViewPriceChart } from '../view-price-chart-link/view-price-chart-link';
import {
  streamPostCardActions,
  streamPostCardContainer,
  streamPostCardFooter,
  streamPostCardLabels,
} from './stream-post-card.css.ts';
import { PostSummary } from './summarize/post-summary.tsx';
import { SummarizePostButton } from './summarize/summarize-post-button.tsx';
import {
  MIN_SUMMARIZABLE_POST_CHARS,
  useSummarizePost,
} from './summarize/use-summarize-post.ts';

export interface StreamPostCardProps {
  streamPost:
    | StreamPostCardFragmentFragment
    | CollectionStreamPostFragmentFragment;
  withAuthor?: boolean;
  cardContainer?: boolean;
  linkToPost?: boolean;
  size?: 'small' | 'medium';
  streamDeckSettings?: StreamDeckSettings;
  highlightPatterns?: HighlightPattern[];
  className?: string;
}

export const StreamPostCard: FC<StreamPostCardProps> = ({
  streamPost,
  withAuthor,
  linkToPost,
  cardContainer,
  size = 'medium',
  streamDeckSettings,
  highlightPatterns,
  className,
}) => {
  const isStreamDeck = Boolean(useMatch(paths.newsDeck));
  const navigate = useNavigate();

  const isDeleted = Boolean(streamPost.deletedAt);

  const Container = cardContainer ? LayoutCard : 'div';

  const arePostInteractionsEnabled = isStreamDeckSettingEnabled(
    'postInteractions',
    streamDeckSettings,
  );

  const isCreatedDateEnabled = isStreamDeckSettingEnabled(
    'createdDate',
    streamDeckSettings,
  );

  const isCommentActionsEnabled = isStreamDeckSettingEnabled(
    'commentActions',
    streamDeckSettings,
  );

  const isStreamLabelEnabled = isStreamDeckSettingEnabled(
    'streamLabel',
    streamDeckSettings,
  );

  const isLinksEnabled = isStreamDeckSettingEnabled(
    'enabledLinks',
    streamDeckSettings,
  );

  const isPostHighlighted = useHighlightNewPost({
    postId: streamPost.id,
    if: isStreamDeck,
  });

  const { toggleSummary, isSummaryVisible, summary, isSummaryLoading } =
    useSummarizePost(streamPost.id);
  const canSummarize =
    !isDeleted && streamPost.text.length >= MIN_SUMMARIZABLE_POST_CHARS;

  const maxBodyChars =
    streamDeckSettings && streamDeckSettings.maxBodyChars !== null
      ? streamDeckSettings.maxBodyChars
      : undefined;
  const shouldCollapseQuoted =
    !!streamDeckSettings && streamDeckSettings.collapseQuotedPosts;
  const { inPost: hasHighlightMatch, inHiddenPost: hasHighlightMatchInHidden } =
    useHighlightMatchSummary({
      postText: streamPost.text,
      patterns: highlightPatterns,
      maxChars: maxBodyChars,
    });

  return (
    <Container
      className={clsx(
        postCardContainer,
        !withAuthor && streamPostCardContainer,
        className,
      )}
      data-highlighted={isPostHighlighted ? '' : undefined}
      data-deleted={isDeleted ? '' : undefined}
      onClick={(e) => {
        if (!arePostInteractionsEnabled) {
          return;
        }
        handlePostCardClick({
          event: e,
          linkToPost,
          navigate,
          postPath: paths.makePost(streamPost.id),
        });
      }}
      role={linkToPost && arePostInteractionsEnabled ? 'link' : undefined}
      tabIndex={linkToPost && arePostInteractionsEnabled ? 0 : undefined}
    >
      {canSummarize ? (
        <SummarizePostButton
          isSummaryVisible={isSummaryVisible}
          onClick={toggleSummary}
          compact={size === 'small'}
        />
      ) : null}

      {withAuthor && 'author' in streamPost ? (
        <PostCardHeader
          isAuthorClickable={arePostInteractionsEnabled}
          author={streamPost.author}
        />
      ) : null}

      <div className={postCardContent}>
        {isDeleted ? (
          <EmptyState
            illustration={<TrashIcon />}
            heading="The post was deleted"
            className={postCardDeletedPlaceholder}
          />
        ) : (
          <>
            {isSummaryVisible ? (
              <PostSummary
                summary={summary}
                loading={isSummaryLoading}
                compact={size === 'small'}
              />
            ) : null}

            <TextEditor
              namespace={`stream-post:${streamPost.id}`}
              className={clsx(postCardBody, {
                [postCardBodyWithDisabledLinks]: !isLinksEnabled,
              })}
              initialState={streamPost.text}
              format="md"
              editable={false}
              theme={size === 'small' ? 'small' : 'base'}
              highlightPatterns={highlightPatterns}
              collapseQuotes={shouldCollapseQuoted}
              forceExpandQuotes={hasHighlightMatch}
              charLimit={maxBodyChars}
              withShowMore={!!maxBodyChars}
              forceExpand={hasHighlightMatchInHidden}
            />

            <PostCardMedia
              media={streamPost.media}
              authorId={streamPost.authorId}
              createdAt={streamPost.createdAt}
            />
          </>
        )}

        {isCreatedDateEnabled && (
          <div className={postCardDateContainer}>
            <time
              title={formatDateWithMs(new Date(streamPost.createdAt))}
              dateTime={streamPost.createdAt}
              className={postCardDate}
            >
              {formatDate(new Date(streamPost.createdAt))}
            </time>
            {!isStreamDeck ? <ViewPriceChart postId={streamPost.id} /> : null}
          </div>
        )}
      </div>

      {(isCommentActionsEnabled || isStreamLabelEnabled) && (
        <div className={clsx(streamPostCardFooter, postCardFooter)}>
          {isCommentActionsEnabled && (
            <div
              className={clsx(streamPostCardActions, postCardActions)}
              onClick={(e) => e.stopPropagation()}
            >
              <PostCardShareActionsMenu post={streamPost} />
            </div>
          )}

          {isStreamLabelEnabled && (
            <div
              className={clsx(postCardLabels, streamPostCardLabels)}
              onClick={(e) => e.stopPropagation()}
            >
              {streamPost.stream.label && streamPost.stream.id ? (
                <StreamLabel
                  label={streamPost.stream.label}
                  streamId={streamPost.stream.id}
                  linkToStream
                />
              ) : null}
            </div>
          )}
        </div>
      )}
    </Container>
  );
};
